import React, { useState, useMemo } from 'react';
import { getBilingualTutorSessionById, getSessionContent, getLanguageOptions, updatePreferredLanguage } from '../data/bilingualTutorData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const BilingualTutorSessionPage = ({ sessionId, language = 'traditional', onBack, onSessionComplete }) => {
  // Load from Supabase
  const { data: remote } = useSupabaseData({
    table: 'eng_bilingualtutor',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // eq: sessionId ? { session_id: sessionId } : undefined
    }
  });

  const session = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getBilingualTutorSessionById(sessionId);
    const rows = Array.isArray(remote) ? remote : [remote];
    const s = rows.find(r => (r.type || r.section) === 'session' && (r.session_id === sessionId || r.id === sessionId)) || rows.find(r => (r.type || r.section) === 'session');
    if (!s) return getBilingualTutorSessionById(sessionId);
    return {
      id: s.session_id || s.id,
      title: { traditional: s.title_tc || '', simplified: s.title_sc || '', english: s.title_en || '' },
      description: { traditional: s.description_tc || '', simplified: s.description_sc || '', english: s.description_en || '' },
      duration: s.duration || '15 min',
      difficulty: s.difficulty || 'beginner'
    };
  }, [remote, sessionId]);

  const content = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getSessionContent(sessionId);
    const rows = Array.isArray(remote) ? remote : [remote];
    const c = rows.find(r => (r.type || r.section) === 'content' && (r.session_id === sessionId || r.id === sessionId)) || rows.find(r => (r.type || r.section) === 'content');
    if (!c) return getSessionContent(sessionId);
    return {
      greeting: { traditional: c.greeting_tc || '', simplified: c.greeting_sc || '', english: c.greeting_en || '' },
      languagePrompt: { traditional: c.lang_prompt_tc || '', simplified: c.lang_prompt_sc || '', english: c.lang_prompt_en || '' },
      lessonIntroduction: { traditional: c.lesson_intro_tc || '', simplified: c.lesson_intro_sc || '', english: c.lesson_intro_en || '' },
      readinessPrompt: { traditional: c.ready_prompt_tc || '', simplified: c.ready_prompt_sc || '', english: c.ready_prompt_en || '' },
      voicePrompt: { traditional: c.voice_prompt_tc || '', simplified: c.voice_prompt_sc || '', english: c.voice_prompt_en || '' }
    };
  }, [remote, sessionId]);

  const languageOptions = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getLanguageOptions();
    const rows = Array.isArray(remote) ? remote : [remote];
    const opts = rows.filter(r => (r.type || r.section) === 'language').map(r => ({
      id: r.lang_id || r.id,
      name: { traditional: r.name_tc || '', simplified: r.name_sc || '', english: r.name_en || '' }
    }));
    return opts.length > 0 ? opts : getLanguageOptions();
  }, [remote]);
  
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [isListening, setIsListening] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userResponse, setUserResponse] = useState('');

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const handleLanguageSelect = (langId) => {
    setSelectedLanguage(langId);
    updatePreferredLanguage(langId);
  };

  const handleVoiceInput = () => {
    setIsListening(!isListening);
    console.log('Voice input toggled:', !isListening);
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      onSessionComplete(sessionId);
    }
  };

  if (!session || !content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="px-6 py-8">
          <button onClick={onBack} className="text-blue-400 mb-4">
            ← {language === 'traditional' ? '返回' : language === 'simplified' ? '返回' : 'Back'}
          </button>
          <div className="text-center py-8">
            <p>{language === 'traditional' ? '課程未找到' : language === 'simplified' ? '课程未找到' : 'Session not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Top Bar */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-white text-sm">9:41</div>
          <button onClick={onBack} className="text-white text-xl">×</button>
        </div>
      </div>

      {/* Central Globe Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 bg-blue-400 rounded-full flex items-center justify-center">
          <span className="text-white text-2xl">🌍</span>
        </div>
      </div>

      {/* Interactive Content */}
      <div className="px-6 space-y-4">
        {/* Greeting Bubble */}
        <div className="bg-white rounded-xl p-4 max-w-xs mx-auto">
          <p className="text-gray-800 text-center">
            {getText(content.greeting)}
          </p>
        </div>

        {/* Language Prompt Bubble */}
        <div className="bg-white rounded-xl p-4 max-w-xs mx-auto">
          <p className="text-gray-800 text-center text-sm">
            {getText(content.languagePrompt)}
          </p>
        </div>

        {/* Language Selection */}
        <div className="text-center mb-4">
          <h3 className="text-white font-semibold mb-3">TEACH ME IN</h3>
          <div className="flex justify-center space-x-3">
            {languageOptions.map((lang) => (
              <button
                key={lang.id}
                onClick={() => handleLanguageSelect(lang.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  selectedLanguage === lang.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {getText(lang.name)}
              </button>
            ))}
          </div>
          <div className="mt-2">
            <span className="inline-block px-2 py-1 bg-gray-600 rounded text-xs text-gray-300">
              {selectedLanguage === 'taiwanese' ? 'Taiwanese' : 'English'}
            </span>
          </div>
        </div>

        {/* Lesson Introduction Bubble */}
        <div className="bg-white rounded-xl p-4 max-w-xs mx-auto">
          <p className="text-gray-800 text-sm leading-relaxed">
            {getText(content.lessonIntroduction)}
          </p>
        </div>

        {/* Readiness Prompt Bubble */}
        <div className="bg-purple-500 rounded-xl p-4 max-w-xs mx-auto">
          <p className="text-white text-center font-medium">
            {getText(content.readinessPrompt)}
          </p>
        </div>

        {/* Voice Input Section */}
        <div className="text-center mt-8">
          <p className="text-white mb-4">
            {getText(content.voicePrompt)}
          </p>
          <button
            onClick={handleVoiceInput}
            className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
              isListening 
                ? 'bg-red-500 animate-pulse' 
                : 'bg-blue-400 hover:bg-blue-500'
            }`}
          >
            <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
            </div>
          </button>
        </div>

        {/* Session Info */}
        <div className="mt-8 text-center">
          <h2 className="text-white font-semibold mb-2">
            {getText(session.title)}
          </h2>
          <p className="text-gray-300 text-sm">
            {getText(session.description)}
          </p>
          <div className="flex justify-center space-x-4 mt-4 text-xs text-gray-400">
            <span>⏱️ {session.duration}</span>
            <span>📚 {session.difficulty.toUpperCase()}</span>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center space-x-4 mt-8">
          <button
            onClick={onBack}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {language === 'traditional' ? '返回' : language === 'simplified' ? '返回' : 'Back'}
          </button>
          <button
            onClick={handleNextStep}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {language === 'traditional' ? '開始' : language === 'simplified' ? '开始' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BilingualTutorSessionPage;
